import { useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Menu, X, Sparkles } from "lucide-react";
import { MaskLines, Eyebrow } from "./primitives";
import { NodeField } from "./NodeField";
import { WaitlistForm } from "./WaitlistForm";

const NAV = [
  { label: "What it does", href: "#what" },
  { label: "Intelligence", href: "#intelligence" },
  { label: "Industries", href: "#industries" },
  { label: "Founding Program", href: "#founding" },
  { label: "About", href: "#about" },
];

const Navbar = ({ onCta }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="fixed inset-x-0 top-0 z-50 flex justify-center px-4 pt-4"
      data-testid="navbar"
    >
      <nav
        className={`flex w-full max-w-7xl items-center justify-between rounded-full px-5 py-3 transition-[background,box-shadow,border] duration-500 ${
          scrolled ? "glass-dark shadow-[0_10px_40px_-20px_rgba(0,0,0,0.8)]" : "border border-transparent"
        }`}
      >
        <a href="#top" className="flex items-center gap-2" data-testid="logo">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-electric text-white">
            <Sparkles className="h-4 w-4" />
          </span>
          <span className="font-serif text-xl tracking-tight text-white">Kyvelra</span>
        </a>

        <div className="hidden items-center gap-8 lg:flex">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className="font-display text-sm text-electric-ice/70 transition-colors duration-300 hover:text-white"
              data-testid={`nav-${n.href.slice(1)}`}
            >
              {n.label}
            </a>
          ))}
        </div>

        <div className="hidden lg:block">
          <button
            onClick={onCta}
            data-testid="nav-cta-btn"
            className="rounded-full bg-white px-5 py-2.5 font-display text-sm font-semibold text-navy-900 transition-transform duration-300 hover:-translate-y-0.5"
          >
            Join the Beta
          </button>
        </div>

        <button
          className="text-white lg:hidden"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-btn"
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-dark absolute top-20 left-4 right-4 rounded-3xl p-6 lg:hidden"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col gap-4">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="font-display text-electric-ice/80"
              >
                {n.label}
              </a>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                onCta();
              }}
              className="mt-2 rounded-full bg-electric px-5 py-3 font-display font-semibold text-white"
            >
              Join the Beta
            </button>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};

export const Hero = ({ onSecondary }) => {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 600], [0, 120]);
  const yContent = useTransform(scrollY, [0, 600], [0, 60]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section
      id="top"
      className="grain relative min-h-screen overflow-hidden bg-navy-950 pt-32"
      data-testid="hero-section"
    >
      <Navbar onCta={() => document.getElementById("signup")?.scrollIntoView({ behavior: "smooth" })} />

      {/* Layered background */}
      <motion.div style={{ y: yBg }} className="pointer-events-none absolute inset-0 z-0">
        {/* flowing gradient waves */}
        <div className="absolute -top-1/3 left-1/2 h-[80vh] w-[80vh] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(47,107,255,0.35),transparent_60%)] blur-3xl animate-gradient-shift" />
        <div className="absolute top-1/4 -left-40 h-[60vh] w-[60vh] rounded-full bg-[radial-gradient(circle,rgba(60,120,255,0.22),transparent_60%)] blur-3xl animate-gradient-shift" style={{ animationDelay: "-6s" }} />
        <div className="absolute bottom-0 right-0 h-[55vh] w-[55vh] rounded-full bg-[radial-gradient(circle,rgba(20,60,160,0.28),transparent_60%)] blur-3xl animate-gradient-shift" style={{ animationDelay: "-12s" }} />
        <div className="dot-grid absolute inset-0 opacity-40" />
      </motion.div>

      {/* connected nodes */}
      <NodeField className="absolute inset-0 z-[1] h-full w-full" />

      {/* content */}
      <motion.div
        style={{ y: yContent, opacity }}
        className="relative z-10 mx-auto max-w-5xl px-6 pb-32 text-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mx-auto mb-8 inline-flex items-center gap-2 rounded-full border border-electric-bright/25 bg-electric/10 px-4 py-1.5"
        >
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-electric-bright" />
          <span className="font-display text-xs tracking-wide text-electric-ice/90">
            Now inviting founding recruiters
          </span>
        </motion.div>

        <h1 className="font-serif text-5xl font-medium leading-[0.95] tracking-tight text-white sm:text-7xl lg:text-[5.5rem]">
          <MaskLines lines={["Recruit Smarter."]} delay={0.35} />
          <MaskLines
            lines={["Discover Faster."]}
            delay={0.5}
            lineClass="italic text-transparent bg-clip-text bg-gradient-to-r from-electric-bright to-electric-ice"
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="mx-auto mt-8 max-w-2xl font-display text-lg text-electric-ice/75 sm:text-xl"
        >
          AI-powered candidate discovery built for modern recruiters.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mx-auto mt-5 max-w-2xl font-body text-base leading-relaxed text-electric-ice/55"
        >
          Kyvelra helps recruiters uncover better candidates, generate sourcing
          strategies, automate recruiting research, and reduce hours of sourcing
          into minutes.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8 }}
          className="mx-auto mt-10 max-w-xl"
          id="hero-signup"
        >
          <WaitlistForm interest="beta" dark testid="hero-waitlist" cta="Join the Beta Waitlist" />
          <div className="mt-5">
            <button
              onClick={onSecondary}
              data-testid="hero-secondary-cta"
              className="group font-display text-sm text-electric-ice/70 transition-colors hover:text-white"
            >
              or become a Founding Recruiter
              <span className="ml-1 inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
            </button>
          </div>
        </motion.div>
      </motion.div>

      {/* bottom fade into light content */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-40 bg-gradient-to-b from-transparent to-background" />
    </section>
  );
};

export default Hero;
